import type { Context, Plugin } from '@nuxt/types';
import type { ApolloQueryResult } from '@apollo/client/core/types';
import type { UiNotification } from '~/composables/useUiNotification';
import type { Pinia } from 'pinia';
import { useCustomerStore } from '~/modules/customer/stores/customer';
import { getBackendCredentialErrorMessage, isBackendCredentialError } from '~/helpers/backendCredentialError';

export const hasGraphqlAuthorizationError = (res: ApolloQueryResult<unknown>) => res?.errors
  ?.some((error) => error?.extensions?.category === 'graphql-authorization') ?? false;

export const hasBackendCredentialAuthorizationError = (res: ApolloQueryResult<unknown>) => {
  return isBackendCredentialError(res?.errors ?? []);
};

const isHttp401Error = (error: any) => {
  return error?.response?.status === 401
    || error?.networkError?.statusCode === 401
    || error?.networkError?.response?.status === 401
    || error?.statusCode === 401;
};

const AUTH_ERROR_MESSAGE_PATTERNS = [
  /status code\s*401/i,
  /composite reader could not read a token/i,
  /not authorized/i,
  /unauthorized/i,
];

const hasAuthMessagePattern = (value?: string) => {
  if (!value) {
    return false;
  }

  return AUTH_ERROR_MESSAGE_PATTERNS.some((pattern) => pattern.test(value));
};

const hasUnauthorizedMessage = (error: any) => {
  const directMessage = error?.message;
  const networkErrorMessage = error?.networkError?.message;
  const graphQLErrorMessages = (error?.graphQLErrors ?? []).map((entry: any) => entry?.message).join(' | ');
  const networkResultErrorMessages = (error?.networkError?.result?.errors ?? []).map((entry: any) => entry?.message).join(' | ');

  return hasAuthMessagePattern(directMessage)
    || hasAuthMessagePattern(networkErrorMessage)
    || hasAuthMessagePattern(graphQLErrorMessages)
    || hasAuthMessagePattern(networkResultErrorMessages);
};

const isUnauthorizedLikeError = (error: any) => {
  return isHttp401Error(error) || hasUnauthorizedMessage(error);
};

type ContextWithPinia = Context & { $pinia: Pinia };

const plugin : Plugin = (context) => {
  const { app, $pinia, redirect } = context as ContextWithPinia & { redirect: (path: string) => void };
  const customerStore = useCustomerStore($pinia);
  if (app.$vsf.$magento.config.state.getCustomerToken()) {
    customerStore.setIsLoggedIn(true);
  }

  const invalidateSession = async () => {
    customerStore.setIsLoggedIn(false);
    customerStore.user = null;
    app.$vsf.$magento.config.state.removeCustomerToken();
    app.$vsf.$magento.config.state.removeCartId();
    app.$vsf.$magento.config.state.setMessage<UiNotification>({
      id: Symbol(''),
      message: app.i18n.t('You are not authorized, please log in.') as string,
      type: 'warning',
      icon: null,
      persist: true,
      title: null,
    });
    // context.redirect works in both SSR (HTTP 302) and client (router push)
    await redirect(app.localePath('/login'));
  };

  const wrapAuthGuard = <T extends (...args: any[]) => Promise<any>>(apiCall: T) => {
    return (async (...args: Parameters<T>) => {
      try {
        return await apiCall(...args);
      } catch (error) {
        if (isUnauthorizedLikeError(error)) {
          await invalidateSession();
          return { data: null, errors: [{ message: 'HTTP 401 Unauthorized' }] };
        }

        throw error;
      }
    }) as T;
  };

  app.$vsf.$magento.api.customQuery = wrapAuthGuard(app.$vsf.$magento.api.customQuery.bind(app.$vsf.$magento.api));
  app.$vsf.$magento.api.customMutation = wrapAuthGuard(app.$vsf.$magento.api.customMutation.bind(app.$vsf.$magento.api));

  // Add catch handler for unhandled Apollo rejections
  if (typeof window !== 'undefined') {
    const handler = async (event: PromiseRejectionEvent) => {
      const reason = event.reason;
      if (isUnauthorizedLikeError(reason)) {
        event.preventDefault();
        await invalidateSession();
      }
    };
    window.addEventListener('unhandledrejection', handler);
  }

  app.$vsf.$magento.client.interceptors.response.use(
    (res) => {
      const responseData = res.data as ApolloQueryResult<unknown>;

      if (hasBackendCredentialAuthorizationError(responseData)) {
        const message = getBackendCredentialErrorMessage(responseData?.errors ?? []);
        app.$vsf.$magento.config.state.setMessage<UiNotification>({
          id: Symbol(''),
          message,
          type: 'danger',
          icon: null,
          persist: true,
          title: null,
        });

        return false;
      }

      if (!hasGraphqlAuthorizationError(responseData)) {
        return res;
      }

      invalidateSession().catch(() => {});
      return false;
    },
    (error) => {
      if (isUnauthorizedLikeError(error)) {
        invalidateSession().catch(() => {});
        return Promise.resolve(false);
      }
      return Promise.reject(error);
    },
  );
};

export default plugin;
